/**
 * Payment Management Module Translations - English
 * Includes: Billing, Payment Plans, Invoice Management
 */
export default {
  paymentMgmt: {
    // ======== Billing Management ========
    billing: {
      title: 'Billing Management',
      subtitle: 'Manage merchant receivable and payable bills',
      addBill: 'Add Bill',
      editBill: 'Edit Bill',
      billList: 'Bill List',
      billNo: 'Bill No.',
      merchantName: 'Merchant Name',
      contractNo: 'Contract No.',
      billType: 'Bill Type',
      billTypeReceivable: 'Receivable',
      billTypePayable: 'Payable',
      amount: 'Amount',
      dueDate: 'Due Date',
      status: 'Status',
      statusPending: 'Pending',
      statusPartialPaid: 'Partially Paid',
      statusPaid: 'Paid',
      statusOverdue: 'Overdue',
      statusCancelled: 'Cancelled',
      remark: 'Remark',
      // Status filter
      filterByStatus: 'Filter by Status',
      allStatus: 'All Status',
      // Actions
      confirmReceipt: 'Confirm Receipt',
      confirmPayment: 'Confirm Payment',
      viewDetail: 'View Detail',
      generateInvoice: 'Generate Invoice',
      // Messages
      fetchFailed: 'Failed to fetch bill list',
      addSuccess: 'Added successfully',
      editSuccess: 'Updated successfully',
      deleteConfirm: 'Are you sure to delete bill "{billNo}"?',
      deleteSuccess: 'Deleted successfully',
      deleteFailed: 'Failed to delete',
      operationSuccess: 'Operation successful',
      operationFailed: 'Operation failed',
    },

    // ======== Payment Plan ========
    paymentPlan: {
      title: 'Payment Plan',
      subtitle: 'Manage contract payment plans',
      generatePlan: 'Generate Plan',
      editPlan: 'Edit Plan',
      planList: 'Plan List',
      planPeriod: 'Plan Period',
      planType: 'Plan Type',
      planTypeReceivable: 'Receivable Plan',
      planTypePayable: 'Payable Plan',
      receivableAmount: 'Receivable Amount',
      payableAmount: 'Payable Amount',
      paidAmount: 'Paid Amount',
      unpaidAmount: 'Unpaid Amount',
      dueDate: 'Due Date',
      paidDate: 'Paid Date',
      invoiceStatus: 'Invoice Status',
      invoiceStatusNone: 'Not Invoiced',
      invoiceStatusApplied: 'Applied',
      invoiceStatusInvoicing: 'Invoicing',
      invoiceStatusInvoiced: 'Invoiced',
      invoiceStatusFailed: 'Failed',
      invoiceStatusRedFlushed: 'Red Flushed',
      merchantConfirmed: 'Merchant Confirmed',
      // Messages
      fetchFailed: 'Failed to fetch plan list',
      generateSuccess: 'Plan generated successfully',
      generateFailed: 'Failed to generate plan',
      editSuccess: 'Updated successfully',
    },

    // ======== Payment Registration ========
    register: {
      title: 'Payment Registration',
      merchantSelect: 'Select Merchant',
      paymentDate: 'Payment Date',
      paymentMethod: 'Payment Method',
      bankReceiptNo: 'Bank Receipt No.',
      receiver: 'Receiver',
      remark: 'Remark',
      billDetail: 'Bill Details',
      selectBill: 'Select Bills',
      billNo: 'Bill No.',
      contractNo: 'Contract No.',
      period: 'Period',
      dueDate: 'Due Date',
      billAmount: 'Bill Amount',
      paidAmount: 'Paid',
      owedAmount: 'Outstanding',
      thisPayment: 'This Payment',
      lateFee: 'Late Fee',
      prepaymentOffset: 'Prepayment Offset',
      availableBalance: 'Available Balance',
      offsetAmount: 'Offset Amount',
      distributeByRatio: 'Distribute by Ratio',
      voucherAndInvoice: 'Vouchers & Invoices',
      totalOwed: 'Total Outstanding',
      totalPayment: 'Total Payment',
      totalOffset: 'Offset',
      cashPayment: 'Cash Payment',
      confirmRegister: 'Confirm Registration',
      cancel: 'Cancel',
      changeMerchant: 'Change Merchant',
      changeMerchantConfirm: 'Changing merchant will clear selected bills and offset. Continue?',
      noMerchantSelected: 'Please select a merchant first',
      noBillSelected: 'Please select at least one bill',
      noPaymentAmount: 'Payment amount and offset amount cannot both be 0',
      overpaymentConfirm: 'The following bills exceed the outstanding amount',
      registerSuccess: 'Payment registered successfully',
      noPrepayment: 'No available prepayment for this merchant',
    },

    // ======== Invoice Management ========
    invoice: {
      title: 'Invoice Management',
      subtitle: 'Manage digital invoice issuance and records',

      // Invoice Request
      request: {
        title: 'Invoice Request',
        subtitle: 'Submit invoice requests',
        addRequest: 'New Request',
        editRequest: 'Edit Request',
        requestNo: 'Request No.',
        requestDate: 'Request Date',
        merchantName: 'Merchant Name',
        totalAmount: 'Invoice Amount',
        status: 'Status',
        statusPending: 'Pending',
        statusProcessing: 'Processing',
        statusDone: 'Completed',
        statusFailed: 'Failed',
        // Invoice Info
        titleType: 'Title Type',
        titleTypeCompany: 'Company',
        titleTypePersonal: 'Personal',
        buyerName: 'Buyer Name',
        buyerTaxNo: 'Tax No.',
        buyerPhone: 'Phone',
        buyerEmail: 'Email',
        deliverType: 'Delivery Method',
        deliverTypeWechat: 'WeChat Card',
        deliverTypeEmail: 'Email',
        deliverTypeBoth: 'Both',
        // Messages
        addSuccess: 'Request submitted successfully',
        editSuccess: 'Updated successfully',
        deleteConfirm: 'Are you sure to delete request "{requestNo}"?',
        fetchFailed: 'Failed to fetch request list',
        submitFailed: 'Failed to submit request',
      },

      // Invoice Processing
      process: {
        title: 'Invoice Processing',
        subtitle: 'Finance team processes invoice requests',
        requestNo: 'Request No.',
        merchantName: 'Merchant Name',
        totalAmount: 'Invoice Amount',
        platformStatus: 'Platform Status',
        platformStatusPending: 'Pending',
        platformStatusProcessing: 'Processing',
        platformStatusSuccess: 'Success',
        platformStatusFailed: 'Failed',
        invoiceType: 'Invoice Type',
        invoiceTypeNormal: 'Digital Normal',
        invoiceTypeSpecial: 'Digital Special',
        // Actions
        issueInvoice: 'Issue Invoice',
        syncStatus: 'Sync Status',
        viewDetail: 'View Detail',
        // Messages
        issueSuccess: 'Invoice issued successfully',
        issueFailed: 'Failed to issue invoice',
        syncSuccess: 'Status synced successfully',
        syncFailed: 'Failed to sync status',
        fetchFailed: 'Failed to fetch pending invoices',
      },

      // Invoice Record
      record: {
        title: 'Invoice Record',
        subtitle: 'View and manage all invoice records',
        recordNo: 'Record No.',
        invoiceNo: 'Invoice No.',
        requestNo: 'Request No.',
        invoiceDate: 'Invoice Date',
        invoiceAmount: 'Invoice Amount',
        invoiceType: 'Invoice Type',
        status: 'Status',
        statusActive: 'Active',
        statusRedFlushed: 'Red Flushed',
        redFlushReason: 'Red Flush Reason',
        redFlushTime: 'Red Flush Time',
        pdfUrl: 'PDF Download',
        qrCode: 'Verification QR Code',
        // Actions
        viewDetail: 'View Detail',
        redFlush: 'Red Flush',
        redFlushConfirm: 'Are you sure to red flush invoice "{invoiceNo}"? This action cannot be undone!',
        redFlushSuccess: 'Red flush successful',
        redFlushFailed: 'Red flush failed',
        deliver: 'Re-deliver',
        deliverSuccess: 'Delivered successfully',
        deliverFailed: 'Failed to deliver',
        // Messages
        fetchFailed: 'Failed to fetch invoice records',
        deleteConfirm: 'Are you sure to delete record "{recordNo}"?',
      },
    },
  },
}
